import type { ReactNode } from 'react';
import type { EmailMessage, Herkunft } from '@epasu/core';
import { CLOSED_PROJECT_STATUSES, type ProjectStatus } from '@epasu/core/constants';
import { ArrowRight, FolderKanban, Mail, Megaphone, Users } from 'lucide-react';
import type { ContactRow, ProjectRow } from '@/lib/crm-client';
import { Card } from '@/components/ui/card';
import { ProjectStatusPill } from '@/components/ProjectStatusPill';
import { formatDate } from '@/lib/utils';

export type UeberblickZiel = 'projects' | 'campaigns' | 'mail' | 'contacts';

interface Props {
  /** `null`, wo es keine Projekte geben kann (Fotografen): die Kachel entfällt. */
  projekte: ProjectRow[] | null;
  istKunde: boolean;
  herkunft: Herkunft | null;
  mails: EmailMessage[];
  kontakte: ContactRow[];
  hauptansprechpartner: string | null;
  onOeffnen: (reiter: UeberblickZiel) => void;
}

/**
 * Der erste Blick auf eine Firma: vier Kacheln, jede führt zum Reiter, der
 * das Detail hält.
 *
 * Nichts davon ist neu erhoben — die Kacheln zeigen, was die Reiter ohnehin
 * laden, und sagen in einem Satz, wo es hinführt: das laufende Projekt, woher
 * der Kunde kam, der letzte Schriftverkehr, die Ansprechpartner. Wo eine Firma
 * zu einer Kachel nichts hat, steht das da, statt dass die Kachel fehlt: ein
 * Kunde ohne Herkunft ist eine Auskunft („direkt angelegt“), keine Lücke.
 * Nur die Projektkachel entfällt, wo es Projekte gar nicht geben kann.
 */
export function UeberblickTab({
  projekte,
  istKunde,
  herkunft,
  mails,
  kontakte,
  hauptansprechpartner,
  onOeffnen,
}: Props) {
  const laufende = (projekte ?? []).filter(
    (p) => !(CLOSED_PROJECT_STATUSES as readonly string[]).includes(p.status),
  );
  const neuesteMail = mails[0];

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {projekte !== null && (
        <Kachel
          icon={FolderKanban}
          titel="Laufendes Projekt"
          ziel="Projekte"
          // Den Reiter gibt es erst für Kunden; vorher führte die Kachel ins Leere.
          onClick={istKunde ? () => onOeffnen('projects') : undefined}
        >
          {!istKunde ? (
            <Leer>Noch kein Kunde, also auch kein Projekt.</Leer>
          ) : laufende.length === 0 ? (
            <Leer>
              {projekte.length === 0
                ? 'Noch kein Projekt.'
                : 'Kein Projekt läuft gerade — alle sind abgeschlossen oder abgebrochen.'}
            </Leer>
          ) : (
            <div className="space-y-1">
              <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                {laufende[0]?.title}
                <ProjectStatusPill status={(laufende[0]?.status ?? 'planung') as ProjectStatus} />
              </p>
              <p className="text-xs text-muted-foreground">
                {laufende[0]?.dueDate
                  ? `Fällig ${formatDate(laufende[0].dueDate)}`
                  : 'Ohne Fälligkeit'}
                {laufende.length > 1 && ` · ${laufende.length - 1} weitere laufen`}
              </p>
            </div>
          )}
        </Kachel>
      )}

      <Kachel
        icon={Megaphone}
        titel="Woher der Kunde kam"
        ziel="Kampagnen"
        onClick={() => onOeffnen('campaigns')}
      >
        {!herkunft ? (
          <Leer>
            {istKunde
              ? 'Direkt als Kunde angelegt — kein Weg über die Akquise.'
              : 'Noch kein Deal in der Akquise.'}
          </Leer>
        ) : herkunft.campaignName ? (
          <div className="space-y-1">
            <p className="text-sm font-medium">{herkunft.campaignName}</p>
            <p className="text-xs text-muted-foreground">
              Kampagne, aus deren Antwort der Deal „{herkunft.dealTitle}“ entstand
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            <p className="text-sm font-medium">Ohne Kampagne</p>
            <p className="text-xs text-muted-foreground">
              Der Deal „{herkunft.dealTitle}“ trägt keine Kampagne
              {herkunft.campaignId && ' — die Kampagne gibt es nicht mehr'}.
            </p>
          </div>
        )}
      </Kachel>

      <Kachel
        icon={Mail}
        titel="Letzter Schriftverkehr"
        ziel="Schriftverkehr"
        onClick={() => onOeffnen('mail')}
      >
        {!neuesteMail ? (
          <Leer>Noch keine Nachrichten.</Leer>
        ) : (
          <div className="space-y-1">
            <p className="truncate text-sm font-medium">
              {neuesteMail.subject || '(ohne Betreff)'}
            </p>
            <p className="text-xs text-muted-foreground">
              {neuesteMail.direction === 'outbound' ? 'Gesendet' : 'Empfangen'}{' '}
              {formatDate(
                neuesteMail.receivedAt ?? neuesteMail.sentAt ?? neuesteMail.createdAt,
              )}
            </p>
          </div>
        )}
      </Kachel>

      <Kachel
        icon={Users}
        titel="Ansprechpartner"
        ziel="Ansprechpartner"
        onClick={() => onOeffnen('contacts')}
      >
        {kontakte.length === 0 ? (
          <Leer>Noch kein Ansprechpartner.</Leer>
        ) : (
          <div className="space-y-1">
            <p className="text-sm font-medium">
              {kontakte.length} Ansprechpartner
              {hauptansprechpartner && ` · Haupt: ${hauptansprechpartner}`}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {kontakte
                .slice(0, 3)
                .map((k) => k.displayName)
                .join(', ')}
              {kontakte.length > 3 && ` und ${kontakte.length - 3} weitere`}
            </p>
          </div>
        )}
      </Kachel>
    </div>
  );
}

function Leer({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function Kachel({
  icon: Icon,
  titel,
  ziel,
  onClick,
  children,
}: {
  icon: typeof Mail;
  titel: string;
  /** Der Name des Reiters, zu dem die Kachel führt. */
  ziel: string;
  /** Ohne Klick-Ziel ist die Kachel nur eine Auskunft. */
  onClick: (() => void) | undefined;
  children: ReactNode;
}) {
  if (!onClick) {
    return (
      <Card className="flex flex-col gap-3 p-5">
        <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
          {titel}
        </span>
        <span className="block">{children}</span>
      </Card>
    );
  }
  return (
    <Card className="p-0">
      <button
        type="button"
        onClick={onClick}
        className="group flex h-full w-full flex-col gap-3 rounded-lg p-5 text-left transition-colors hover:bg-muted/40"
      >
        <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
          {titel}
        </span>
        <span className="block">{children}</span>
        <span className="mt-auto flex items-center gap-1 text-xs text-muted-foreground group-hover:text-foreground">
          Zum Reiter „{ziel}“
          <ArrowRight className="h-3 w-3" />
        </span>
      </button>
    </Card>
  );
}
